// src/components/QuotaPanel.js
// Marker: quota_v1
// Renders a quota status row to be placed below each LINE channel card

import React, { useState } from 'react';
import API from '../api';
import { RefreshCw } from 'lucide-react';

const STATUS_COLORS = {
  ok:       { bg: '#d1fae5', text: '#065f46', label: '✅ ปกติ' },
  warning:  { bg: '#fef3c7', text: '#92400e', label: '⚠️ ใกล้หมด' },
  critical: { bg: '#fee2e2', text: '#991b1b', label: '🔴 วิกฤต' },
  exceeded: { bg: '#fecaca', text: '#7f1d1d', label: '❌ เกินโควต้า' },
  error:    { bg: '#e5e7eb', text: '#374151', label: '⚙️ ผิดพลาด' },
  unknown:  { bg: '#f3f4f6', text: '#6b7280', label: '❓ ยังไม่เช็ค' },
};

const TYPE_LABELS = {
  free:     'Free (300/เดือน)',
  light:    'Light (15K/เดือน)',
  standard: 'Standard (45K/เดือน)',
  premium:  'Premium (ไม่จำกัด)',
  unknown:  'ไม่ทราบ Plan',
};

function formatTime(iso) {
  if (!iso) return 'ยังไม่เคยเช็ค';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'เพิ่งเช็ค';
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} ชม.ที่แล้ว`;
  return d.toLocaleDateString('th-TH');
}

export default function QuotaPanel({ channel, onUpdate }) {
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [local, setLocal] = useState(channel);

  // re-sync if parent updates (e.g. after onUpdate refetch)
  React.useEffect(() => { setLocal(channel); }, [channel]);

  const status = local.quota_status || 'unknown';
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const used = local.quota_used || 0;
  const limit = local.quota_limit || 0;
  const isPremium = local.quota_type === 'premium';
  const ratio = limit > 0 ? Math.min(used / limit, 1) : 0;
  const percent = (ratio * 100).toFixed(1);

  async function handleRefresh() {
    setRefreshing(true);
    setError(null);
    try {
      const res = await API.post(`/api/quota/refresh/${local.id}`);
      // update local state immediately
      setLocal({
        ...local,
        quota_status: res.data.status,
        quota_used: res.data.used,
        quota_limit: res.data.limit,
        quota_type: res.data.type,
        quota_checked_at: new Date().toISOString(),
        quota_error: null,
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRefreshing(false);
    }
  }

  const barColor = status === 'exceeded' || status === 'critical' ? '#dc2626'
                 : status === 'warning' ? '#f59e0b'
                 : status === 'error' ? '#9ca3af'
                 : '#10b981';

  return (
    <div style={{
      background: '#f8fafc',
      borderRadius: 8,
      padding: '10px 14px',
      marginTop: 10,
      border: '1px solid #e2e8f0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            background: color.bg,
            color: color.text,
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
          }}>
            {color.label}
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {TYPE_LABELS[local.quota_type] || local.quota_type || 'ยังไม่ทราบ'}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh quota"
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '4px 8px',
            cursor: refreshing ? 'wait' : 'pointer',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <RefreshCw size={12} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
          {refreshing ? 'กำลังเช็ค...' : 'Refresh'}
        </button>
      </div>

      {!isPremium && limit > 0 && (
        <>
          <div style={{
            height: 8,
            background: '#e2e8f0',
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: 4,
          }}>
            <div style={{
              width: `${percent}%`,
              height: '100%',
              background: barColor,
              transition: 'width 0.3s',
            }} />
          </div>
          <div style={{ fontSize: 12, color: '#475569', display: 'flex', justifyContent: 'space-between' }}>
            <span>ใช้: <b>{used.toLocaleString()}</b> / {limit.toLocaleString()}</span>
            <span style={{ color: barColor, fontWeight: 600 }}>{percent}%</span>
          </div>
        </>
      )}

      {isPremium && (
        <div style={{ fontSize: 12, color: '#475569' }}>
          ใช้: <b>{used.toLocaleString()}</b> ข้อความ (ไม่จำกัด)
        </div>
      )}

      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
        เช็คล่าสุด: {formatTime(local.quota_checked_at)}
      </div>

      {local.quota_error && (
        <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
          ⚠️ {local.quota_error.slice(0, 120)}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
          ❌ {error}
        </div>
      )}
    </div>
  );
}
